import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import noop from 'lodash/noop';
import { getQuestionNotificationSummary } from '../../helpers';
import { DAY_OF_WEEK_LIST, HOURS_OPTIONS } from '../common/constants';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import IndeterminateCheckbox from '../common/IndeterminateCheckbox';
import SelectField from '../common/SelectField';
import { ITimezone } from '@framework/timezone/vo/Timezone';
import {
  IQuestionsNotificationSettingsPayload,
  IQuestionsRealtimeNotificationSettingsPayload,
} from '../../domain/vo/QuestionsEmailAlerts';
import type { IndeterminateValue } from '../../domain/vo/IndeterminateValue';
import RealTime from './RealTime';
import styles from './questionAlerts.module.scss';

interface IProps<T, R> {
  isFetching?: boolean,
  timezone?: ITimezone,
  initialValues?: T,
  initialRealtimeValues: R,
  name?: string,
  isDisabled?: boolean,
  onChange?: (values: T) => void,
  onRealtimeChange?: (values: R) => void,
  isQuestionManager: boolean,
  realtimeError: string,
  setRealtimeError: (errorMessage: string) => void,
  isNoneCheckboxVisible?: boolean,
}

const QuestionAlerts = <
  T extends IndeterminateValue<IQuestionsNotificationSettingsPayload>,
  R extends IndeterminateValue<IQuestionsRealtimeNotificationSettingsPayload>,
>({
  name = 'questionAlertsForm',
  initialValues,
  initialRealtimeValues,
  isFetching = false,
  isDisabled,
  timezone,
  onChange = noop,
  onRealtimeChange = noop,
  isQuestionManager,
  realtimeError,
  setRealtimeError,
  isNoneCheckboxVisible,
}: IProps<T, R>) => {
  const [questionAlertsState, setQuestionAlertsState] = useState<T>(initialValues!);
  const [realtimeAlertsState, setRealtimeAlertsState] = useState<R>(initialRealtimeValues);

  useEffect(() => {
    onChange(questionAlertsState);
  }, [questionAlertsState]);

  useEffect(() => {
    onRealtimeChange(realtimeAlertsState);
  }, [realtimeAlertsState]);

  useEffect(() => {
    setQuestionAlertsState(initialValues!);
  }, [initialValues, isDisabled]);

  const validateRealtime = (realtimeAlerts: R) => {
    if (
      !realtimeAlerts.questionSubmitted &&
      !realtimeAlerts.replySubmitted &&
      !(isQuestionManager && (realtimeAlerts.noteSubmitted || realtimeAlerts.questionAssigned))
    ) {
      setRealtimeError('Select at least one item');
    } else {
      setRealtimeError('');
    }
  };

  useEffect(() => {
    if (questionAlertsState.isQuestionRealTime) {
      validateRealtime(realtimeAlertsState);
    } else {
      setRealtimeError('');
    }
  }, [questionAlertsState.isQuestionRealTime]);

  const handleCheckboxFieldChange = (
    field: keyof T & `is${ string }`,
    value: boolean,
  ) => {
    const state = {
      ...questionAlertsState,
      [field]: value,
    };

    if (field === 'isActiveQuestionAlerts') {
      state.isActiveQuestionAlerts = true;
      state.isQuestionRealTime = false;
      state.isQuestionDaily = false;
      state.isQuestionWeekly = false;
    } else {
      state.isActiveQuestionAlerts = false;
    }

    if (
      state.isActiveQuestionAlerts === false &&
      state.isQuestionDaily === false &&
      state.isQuestionWeekly === false &&
      state.isQuestionRealTime === false
    ) {
      state.isActiveQuestionAlerts = true;
    }

    setQuestionAlertsState(state);
  };

  const handleRealtimeCheckboxChange = (
    field: keyof R,
    value: boolean,
  ) => {
    const state = {
      ...realtimeAlertsState,
      [field]: value,
    };

    validateRealtime(state);

    setRealtimeAlertsState(state);
  };

  const handleSelectFieldChange = (
    name: keyof Omit<T, keyof T & `is${ string }`>,
    value: T[keyof Omit<T, keyof T & `is${ string }`>],
  ) => {
    if (!value) return;

    setQuestionAlertsState({
      ...questionAlertsState,
      [name]: value,
    });
  };

  return (
    <div data-test={ name }>
      { isNoneCheckboxVisible && (
        <div className={ styles.itemAlerts }>
          <IndeterminateCheckbox
            name="isActiveQuestionAlerts"
            dataTest="noneCheckbox"
            label="None"
            disabled={ isDisabled }
            initialValue={ initialValues!.isActiveQuestionAlerts }
            value={ questionAlertsState.isActiveQuestionAlerts }
            onChange={ (value) => handleCheckboxFieldChange('isActiveQuestionAlerts' as keyof T & `is${ string }`, value) }
            className={ styles.checkbox }
          />
        </div>
      ) }
      <div className={ styles.itemAlerts }>
        <IndeterminateCheckbox
          name="isQuestionRealTime"
          dataTest="realtimeCheckbox"
          label="Real-time"
          disabled={ isDisabled }
          initialValue={ initialValues!.isQuestionRealTime }
          value={ questionAlertsState.isQuestionRealTime }
          onChange={ (value) => handleCheckboxFieldChange('isQuestionRealTime' as keyof T & `is${ string }`, value) }
          className={ styles.checkbox }
        />
        { questionAlertsState.isQuestionRealTime && (
          <RealTime
            values={ realtimeAlertsState }
            initialValues={ initialRealtimeValues }
            handleChange={ handleRealtimeCheckboxChange as any }
            isQuestionManager={ isQuestionManager }
            realtimeError={ realtimeError }
          />
        ) }
      </div>
      <div className={ styles.itemAlerts }>
        <IndeterminateCheckbox
          name="isQuestionDaily"
          dataTest="dailyCheckbox"
          label="Daily summary"
          disabled={ isDisabled }
          initialValue={ initialValues!.isQuestionDaily }
          value={ questionAlertsState.isQuestionDaily }
          onChange={ (value) => handleCheckboxFieldChange('isQuestionDaily' as keyof T & `is${ string }`, value) }
          className={ cn(styles.checkbox, styles.dailyTimeCheckbox) }
        />
        <div className={ cn(styles.inlineBlock, styles.wrpSelect) }>
          <SelectField<T>
            name="questionDailyTime"
            dataTest="questionDailyTimeSelect"
            selectClassName={ styles.timeSelect }
            options={ HOURS_OPTIONS }
            disabled={ !questionAlertsState.isQuestionDaily || !!isDisabled }
            value={ questionAlertsState.questionDailyTime }
            onChange={ handleSelectFieldChange as any }
          />
        </div>
      </div>
      <div className={ styles.itemAlerts }>
        <IndeterminateCheckbox
          name="isQuestionWeekly"
          dataTest="weeklyCheckbox"
          label="Weekly summary"
          disabled={ isDisabled }
          initialValue={ initialValues!.isQuestionWeekly }
          value={ questionAlertsState.isQuestionWeekly }
          onChange={ (value) => handleCheckboxFieldChange('isQuestionWeekly' as keyof T & `is${ string }`, value) }
          className={ styles.checkbox }
        />
        <div className={ cn(styles.inlineBlock, styles.wrpSelect) }>
          <SelectField<T>
            name="questionWeeklyTime"
            dataTest="questionWeeklyTimeSelect"
            selectClassName={ styles.timeSelect }
            options={ HOURS_OPTIONS }
            disabled={ !questionAlertsState.isQuestionWeekly || !!isDisabled }
            value={ questionAlertsState.questionWeeklyTime }
            onChange={ handleSelectFieldChange as any }
          />
        </div>
        <div
          className={ cn(
            styles.inlineBlock,
            styles.dayWeek,
          ) }
        >
          <SelectField<T>
            name="questionWeeklyDay"
            dataTest="questionWeeklyDaySelect"
            selectClassName={ styles.selectDaysOfWeek }
            options={ DAY_OF_WEEK_LIST }
            disabled={ !questionAlertsState.isQuestionWeekly || !!isDisabled }
            value={ questionAlertsState.questionWeeklyDay }
            onChange={ handleSelectFieldChange as any }
          />
        </div>
      </div>
      { timezone && (
        <div className={ styles.footer }>
          <span className={ styles.strong }>
            Summary:
          </span> { getQuestionNotificationSummary(questionAlertsState, timezone.abbr) }
        </div>
      ) }
      <Spinner
        overlay
        isVisible={ isFetching }
      />
    </div>
  );
};

export default QuestionAlerts;
